import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../../ui/card';
import { Button } from '../../ui/button';
import { TooltipProvider } from '../../ui/tooltip';
import { Binary, Plus, RotateCcw, Sparkles, ArrowDown, AlertTriangle } from 'lucide-react';
import RawFrameCard from './RawFrameCard';
import DbcPanel from './DbcPanel';
import { canFrameDecoderToolApi } from '../../../lib/toolsApi';
import {
  DEFAULT_FRAMES, EXAMPLE_FRAMES, MAX_FRAMES, newFrame, toPayloadFrame, withIds,
} from '../../../lib/canFrameDecoder';

const DEBOUNCE_MS = 450;

const CanFrameDecoder = () => {
  const [frames, setFrames] = useState(() => withIds(DEFAULT_FRAMES));
  const [outcome, setOutcome] = useState(null); // { byId, dbc, validation }
  const [loading, setLoading] = useState(false);
  const [requestError, setRequestError] = useState(null);
  const [pausedForS, setPausedForS] = useState(0); // >0 while rate limited (countdown)
  const requestIdRef = useRef(0);
  const pausedUntilRef = useRef(0);
  const retryTimerRef = useRef(null);
  const latestRef = useRef({ key: '', ids: [] });

  const payloadKey = useMemo(() => JSON.stringify({ frames: frames.map(toPayloadFrame) }), [frames]);
  const idsKey = frames.map((f) => f.id).join(',');

  // Frame ids at request time, so responses map back to the right cards even if frames
  // were added or removed while the request was in flight.
  const request = useCallback((key, ids) => {
    const requestId = ++requestIdRef.current;
    setLoading(true);
    return canFrameDecoderToolApi.run(JSON.parse(key))
      .then((res) => {
        if (requestId !== requestIdRef.current) return null;
        setLoading(false);
        if (res.rateLimited) {
          // Keep the last results on screen and retry the newest inputs once allowed.
          pausedUntilRef.current = Date.now() + res.retryAfterS * 1000;
          setPausedForS(res.retryAfterS);
          clearTimeout(retryTimerRef.current);
          retryTimerRef.current = setTimeout(() => {
            pausedUntilRef.current = 0;
            setPausedForS(0);
            request(latestRef.current.key, latestRef.current.ids);
          }, res.retryAfterS * 1000);
          return null;
        }
        if (res.errors) {
          setRequestError(res.error || Object.values(res.errors)[0]);
          return null;
        }
        const { frames: frameResults, dbc, validation } = res.results;
        const byId = Object.fromEntries(frameResults.map((r, i) => [ids[i], r]));
        const issues = validation.issues.map((iss) => ({ ...iss, frameIds: iss.frames.map((i) => ids[i]) }));
        const next = { byId, dbc, validation: { ...validation, issues } };
        setOutcome(next);
        setRequestError(null);
        return next;
      })
      .catch((e) => {
        if (requestId !== requestIdRef.current) return null;
        setLoading(false);
        setRequestError(e.message);
        return null;
      });
  }, []);

  useEffect(() => {
    const ids = idsKey.split(',');
    latestRef.current = { key: payloadKey, ids };
    // While rate limited, the scheduled retry picks up these latest inputs.
    if (Date.now() < pausedUntilRef.current) return undefined;
    const timer = setTimeout(() => request(payloadKey, ids), DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [payloadKey, idsKey, request]);

  useEffect(() => {
    if (pausedForS <= 0) return undefined;
    const t = setTimeout(() => setPausedForS((v) => Math.max(0, v - 1)), 1000);
    return () => clearTimeout(t);
  }, [pausedForS]);

  useEffect(() => () => clearTimeout(retryTimerRef.current), []);

  const updateFrame = (id, patch) => setFrames((prev) => prev.map((f) => (f.id === id ? { ...f, ...patch } : f)));
  const updateSignal = (id, patch) => setFrames((prev) => prev.map((f) => (f.id === id ? { ...f, signal: { ...f.signal, ...patch } } : f)));
  const removeFrame = (id) => setFrames((prev) => prev.filter((f) => f.id !== id));
  const addFrame = () => {
    if (frames.length >= MAX_FRAMES) return;
    const frame = newFrame(frames);
    setFrames((prev) => [...prev, frame]);
    setTimeout(() => document.getElementById(`raw-frame-${frame.id}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
  };
  const loadExample = () => {
    setFrames(withIds(EXAMPLE_FRAMES));
    toast.success('Example frames loaded: Intel, Motorola (signed) and a 64-byte CAN FD frame.');
  };
  const reset = () => setFrames(withIds(DEFAULT_FRAMES));

  const jumpToFrame = (id) => {
    document.getElementById(`raw-frame-${id}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const validateNow = async () => {
    if (Date.now() < pausedUntilRef.current) {
      toast.info(`Too many updates in a short time; validation resumes in ${pausedForS} s.`);
      return;
    }
    const next = await request(payloadKey, idsKey.split(','));
    document.getElementById('cfd-dbc-panel')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    if (!next) return;
    const { passed, errorCount } = next.validation;
    if (passed) toast.success('✓ DBC Validation Passed');
    else toast.error(`✕ DBC Validation Failed — ${errorCount} Issue${errorCount === 1 ? '' : 's'} Found`);
  };

  // Issues use frame ids; the panel works with the current frame numbering.
  const indexById = Object.fromEntries(frames.map((f, i) => [f.id, i]));
  const panelValidation = outcome && {
    ...outcome.validation,
    issues: outcome.validation.issues.map((iss) => ({
      ...iss,
      frames: iss.frameIds.filter((id) => id in indexById).map((id) => indexById[id]),
    })),
  };

  const siblingsFor = (frame) => {
    const mine = outcome?.byId[frame.id];
    if (!mine || mine.dbcId === null || mine.dbcId === undefined) return [];
    return frames
      .filter((f) => f.id !== frame.id && outcome.byId[f.id]?.dbcId === mine.dbcId && outcome.byId[f.id]?.layout)
      .map((f) => ({ name: f.signal.name || 'Unnamed', bits: outcome.byId[f.id].layout.bits }));
  };

  return (
    <TooltipProvider>
      <div className="space-y-6" data-testid="can-frame-decoder">
        <Card className="technical-card">
          <CardHeader>
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
              <div>
                <CardTitle className="font-chivo uppercase tracking-tight flex items-center gap-2">
                  <Binary className="h-5 w-5 text-autoxium-blue" />
                  CAN Raw Frame Decoder
                </CardTitle>
                <CardDescription>
                  Raw CAN frame → signal definition → decode → bit visualization → DBC → validation → download.
                  Everything updates live as you edit.
                </CardDescription>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <Button type="button" variant="outline" size="sm" onClick={loadExample} data-testid="cfd-example-btn">
                  <Sparkles className="h-3.5 w-3.5" /> Example
                </Button>
                <Button type="button" variant="outline" size="sm" onClick={reset} data-testid="cfd-reset-btn">
                  <RotateCcw className="h-3.5 w-3.5" /> Reset
                </Button>
              </div>
            </div>
          </CardHeader>
        </Card>

        {frames.map((frame, i) => (
          <RawFrameCard
            key={frame.id}
            frame={frame}
            index={i}
            result={outcome?.byId[frame.id]}
            siblings={siblingsFor(frame)}
            issues={(panelValidation?.issues || []).filter((iss) => iss.frames.includes(i))}
            onChange={(patch) => updateFrame(frame.id, patch)}
            onSignalChange={(patch) => updateSignal(frame.id, patch)}
            onRemove={() => removeFrame(frame.id)}
            canRemove={frames.length > 1}
          />
        ))}

        <div className="flex flex-col items-center gap-2">
          <Button type="button" variant="outline" onClick={addFrame} disabled={frames.length >= MAX_FRAMES} className="border-dashed border-2 w-full sm:w-auto" data-testid="cfd-add-frame">
            <Plus className="h-4 w-4" /> Add Raw Value
          </Button>
          <p className="text-xs text-muted-foreground text-center">
            {frames.length} of {MAX_FRAMES} raw frames. Frames with the same CAN ID become one DBC message with one signal each.
          </p>
          <ArrowDown className="h-5 w-5 text-muted-foreground/60" aria-hidden="true" />
        </div>

        {pausedForS > 0 && (
          <Card className="technical-card"><CardContent className="pt-6">
            <div className="flex items-start gap-2 text-sm text-amber-700 dark:text-amber-400" data-testid="cfd-rate-limited">
              <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
              <span>
                Live updates are paused because of too many changes in a short time. Results below may not match
                your latest edits; they refresh automatically in {pausedForS} s.
              </span>
            </div>
          </CardContent></Card>
        )}

        {requestError && (
          <Card className="technical-card"><CardContent className="pt-6">
            <div className="flex items-start gap-2 text-sm text-destructive" data-testid="cfd-request-error">
              <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" /> <span>{requestError}</span>
            </div>
          </CardContent></Card>
        )}

        <DbcPanel
          dbc={outcome?.dbc}
          validation={panelValidation}
          loading={loading}
          onValidate={validateNow}
          onJumpToFrame={(i) => frames[i] && jumpToFrame(frames[i].id)}
        />
      </div>
    </TooltipProvider>
  );
};

export default CanFrameDecoder;
